import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Checkbox,
  Text,
  Box,
  useToast,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  IconButton,
  Progress,
  Spinner,
  Tooltip,
  SimpleGrid,
  Input,
  FormControl,
  FormLabel,
  Select
} from '@chakra-ui/react';
import { 
  MdUploadFile, 
  MdDownload, 
  MdVisibility, 
  MdDelete,
  MdCheckCircle,
  MdError 
} from 'react-icons/md';
import api from '../../../../../services/api';
import documentService from '../../../../../services/documentService';
import DocumentViewer from '../../../../../components/DocumentViewer';
import RiskAnalysisModal from '../../../../../components/RiskAnalysisModal';
import CertidaoEstadualModal from '../../../../../components/CertidaoEstadualModal';

const DocumentChecklistModal = ({ isOpen, onClose, precatorio, onUpdate, state }) => {
  const [checklist, setChecklist] = useState({});
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [viewDocument, setViewDocument] = useState(null);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [generatingCertidao, setGeneratingCertidao] = useState(false);
  const [generatingCertidaoEstadual, setGeneratingCertidaoEstadual] = useState(false);
  const [riskAnalysisData, setRiskAnalysisData] = useState(null);
  const [showRiskAnalysis, setShowRiskAnalysis] = useState(false);
  const [loadingRiskAnalysis, setLoadingRiskAnalysis] = useState(false);
  const [certidaoEstadualData, setCertidaoEstadualData] = useState(null);
  const [showCertidaoEstadual, setShowCertidaoEstadual] = useState(false);
  const [customCpfEstadual, setCustomCpfEstadual] = useState('');
  const [customCpfFederal, setCustomCpfFederal] = useState('');
  const [generatingCertidaoTrabalhista, setGeneratingCertidaoTrabalhista] = useState(false);
  const [customCpfTrabalhista, setCustomCpfTrabalhista] = useState('');
  const [selectedRegiao, setSelectedRegiao] = useState('');
  const fileInputRefs = useRef({});
  const toast = useToast();

  const cpfOabDocuments = {
    rg_cpf_cnh: 'RG, CPF ou CNH',
    comprovante_residencia: 'Comprovante de Residência',
    certidao_casamento: 'Certidão de Casamento (se aplicável)',
    dados_bancarios: 'Dados Bancários',
    contrato_honorarios: 'Contrato de Honorários',
    selfie_documento: 'Selfie com Documento',
    certidao_estadual: 'Certidão Estadual',
    certidao_federal: 'Certidão Federal',
    certidao_trabalhista: 'Certidão Trabalhista (opcional)',
    certidao_civel: 'Certidão Cível (opcional)',
    certidao_insolvencia: 'Certidão Insolvência (opcional)',
    processo_execucao: 'Processo de Execução',
    oficio_requisitorio: 'Ofício Requisitório (opcional)',
    planilha_calculos: 'Planilha Cálculos (opcional)'
  };

  const cnpjDocuments = {
    contrato_social: 'Contrato Social',
    certidao_inteiro_teor: 'Certidão de Inteiro Teor',
    rg_cpf_socios: 'RG/CPF dos Sócios',
    comprovante_endereco_empresa: 'Comprovante de Endereço da Empresa',
    dados_bancarios_empresa: 'Dados Bancários da Empresa',
    certidao_trabalhista: 'Certidão Trabalhista (opcional)',
    certidao_civel: 'Certidão Cível (opcional)',
    certidao_insolvencia: 'Certidão Insolvência (opcional)',
    processo_execucao: 'Processo de Execução',
    oficio_requisitorio: 'Ofício Requisitório (opcional)',
    planilha_calculos: 'Planilha Cálculos (opcional)'
  };

  useEffect(() => {
    if (precatorio && isOpen) {
      // Initialize checklist with existing values or default false values
      const requiredDocs = precatorio.document_type_used === 'cnpj' ? cnpjDocuments : cpfOabDocuments;
      const initialChecklist = {};
      
      // Set default false for all required documents
      Object.keys(requiredDocs).forEach(key => {
        initialChecklist[key] = false;
      });
      
      // Override with saved values if they exist
      if (precatorio.checklist) {
        Object.keys(precatorio.checklist).forEach(key => {
          if (key in initialChecklist) {
            initialChecklist[key] = precatorio.checklist[key];
          }
        });
      }
      
      setChecklist(initialChecklist);

      // Initialize custom CPF fields with precatorio's CPF
      if (precatorio.cpf_cnpj) {
        setCustomCpfEstadual(precatorio.cpf_cnpj);
        setCustomCpfFederal(precatorio.cpf_cnpj);
        setCustomCpfTrabalhista(precatorio.cpf_cnpj);
      }

      // Fetch documents list
      fetchDocuments();
    }
  }, [precatorio, isOpen]);

  const fetchDocuments = async () => {
    try {
      setLoadingDocs(true);
      const response = await documentService.getDocumentsList(state, precatorio._id);
      if (response.success && response.data) {
        setDocuments(response.data.documents || {});
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const getRequiredDocuments = () => {
    // Default to CPF/OAB documents when document_type_used is not set
    return precatorio?.document_type_used === 'cnpj' ? cnpjDocuments : cpfOabDocuments;
  };

  // Removed manual checkbox change - checkboxes are now read-only
  // and automatically reflect document upload status

  const handleFileUpload = async (docType, file) => {
    if (!file) return;

    const validation = documentService.validateFile(file);
    if (!validation.valid) {
      toast({
        title: 'Arquivo inválido',
        description: validation.error,
        status: 'error',
        duration: 5000
      });
      return;
    }

    setUploadingDoc(docType);

    try {
      const response = await documentService.uploadDocument(
        state,
        precatorio._id,
        docType,
        file
      );

      if (response.success) {
        toast({
          title: 'Documento enviado',
          description: `${file.name} foi enviado com sucesso.`,
          status: 'success',
          duration: 3000
        });

        // Update local state
        setChecklist(prev => ({ ...prev, [docType]: true }));
        
        // Refresh documents list
        await fetchDocuments();
        
        // Call onUpdate if all documents are complete
        if (response.data?.checklistCompleted) {
          onUpdate(precatorio);
        }
      }
    } catch (error) {
      toast({
        title: 'Erro ao enviar documento',
        description: error.message || 'Erro ao enviar arquivo',
        status: 'error',
        duration: 5000
      });
    } finally {
      setUploadingDoc(null);
      // Clear file input
      if (fileInputRefs.current[docType]) {
        fileInputRefs.current[docType].value = '';
      }
    }
  };

  const handleViewDocument = async (docType) => {
    try {
      const response = await documentService.getDocumentUrl(
        state,
        precatorio._id,
        docType
      );

      if (response.success && response.data) {
        setViewDocument({
          url: response.data.url,
          fileName: response.data.fileName,
          fileType: response.data.fileType
        });
      }
    } catch (error) {
      toast({
        title: 'Erro ao visualizar documento',
        description: error.message || 'Não foi possível abrir o documento',
        status: 'error',
        duration: 5000
      });
    }
  };

  const handleDownloadDocument = async (docType) => {
    try {
      const doc = documents[docType];
      await documentService.downloadDocument(
        state,
        precatorio._id,
        docType,
        doc?.fileName || `documento_${docType}`
      );
      
      toast({
        title: 'Download iniciado',
        description: 'O arquivo está sendo baixado.',
        status: 'info',
        duration: 3000
      });
    } catch (error) {
      toast({
        title: 'Erro ao baixar documento',
        description: error.message || 'Não foi possível baixar o arquivo',
        status: 'error',
        duration: 5000
      });
    }
  };

  const handleDeleteDocument = async (docType) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) {
      return;
    }

    try {
      const response = await documentService.deleteDocument(
        state,
        precatorio._id,
        docType
      );

      if (response.success) {
        toast({
          title: 'Documento excluído',
          description: 'O documento foi removido com sucesso.',
          status: 'success',
          duration: 3000
        });

        // Update local state
        setChecklist(prev => ({ ...prev, [docType]: false }));
        
        // Refresh documents list
        await fetchDocuments();
      }
    } catch (error) {
      toast({
        title: 'Erro ao excluir documento',
        description: error.message || 'Não foi possível excluir o arquivo',
        status: 'error',
        duration: 5000
      });
    }
  };

  const handleViewRiskAnalysis = async () => {
    setLoadingRiskAnalysis(true);
    try {
      const response = await documentService.getRiskAnalysis(state, precatorio._id);
      if (response.success && response.data) {
        setRiskAnalysisData(response.data);
        setShowRiskAnalysis(true);
      }
    } catch (error) {
      toast({
        title: 'Erro ao carregar análise de risco',
        description: error.message || 'Não foi possível carregar a análise',
        status: 'error',
        duration: 5000
      });
    } finally {
      setLoadingRiskAnalysis(false);
    }
  };

  const handleGenerateCertidaoFederal = async () => {
    setGeneratingCertidao(true);

    try {
      const response = await api.post(
        `/contacted-precatorios/${state}/${precatorio._id}/certidao-federal/generate`,
        { cpfCnpj: customCpfFederal }
      );

      if (response.success) {
        toast({
          title: 'Certidão Federal gerada',
          description: 'A Certidão Federal foi gerada e anexada com sucesso.',
          status: 'success',
          duration: 5000
        });

        // Update local state
        setChecklist(prev => ({ ...prev, certidao_federal: true }));
        
        // Refresh documents list
        await fetchDocuments();
      } else if (response.compromised) {
        // Certidão compromissada
        toast({
          title: 'Certidão Federal compromissada',
          description: response.message || 'Cliente com pendências fiscais.',
          status: 'warning',
          duration: 7000,
          isClosable: true
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao gerar certidão';
      
      // Check if it's a compromised certidão
      if (error.response?.data?.compromised) {
        toast({
          title: 'Certidão Federal compromissada',
          description: 'Cliente com pendências fiscais.',
          status: 'warning',
          duration: 7000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Erro ao gerar Certidão Federal',
          description: errorMessage,
          status: 'error',
          duration: 5000
        });
      }
    } finally {
      setGeneratingCertidao(false);
    }
  };

  const handleGenerateCertidaoEstadual = async () => {
    setGeneratingCertidaoEstadual(true);

    try {
      const response = await api.post(
        `/contacted-precatorios/${state}/${precatorio._id}/certidao-estadual/generate`,
        { cpfCnpj: customCpfEstadual }
      );

      if (response.success) {
        toast({
          title: 'Certidão Estadual gerada',
          description: 'A Certidão Estadual foi gerada e anexada com sucesso.',
          status: 'success',
          duration: 5000
        });

        // Update local state
        setChecklist(prev => ({ ...prev, certidao_estadual: true }));

        // Refresh documents list
        await fetchDocuments();
      } else if (response.compromised) {
        // Certidão compromissada
        const debitosMsg = response.debitos && response.debitos.length > 0
          ? ` Débitos: ${response.debitos.join(', ')}`
          : '';

        toast({
          title: 'Certidão Estadual compromissada',
          description: (response.message || 'Cliente com pendências fiscais estaduais.') + debitosMsg,
          status: 'warning',
          duration: 10000,
          isClosable: true
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao gerar certidão';

      // Check if it's a compromised certidão
      if (error.response?.data?.compromised) {
        const debitosMsg = error.response?.data?.debitos && error.response.data.debitos.length > 0
          ? ` Débitos: ${error.response.data.debitos.join(', ')}`
          : '';

        toast({
          title: 'Certidão Estadual compromissada',
          description: 'Cliente com pendências fiscais estaduais.' + debitosMsg,
          status: 'warning',
          duration: 10000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Erro ao gerar Certidão Estadual',
          description: errorMessage,
          status: 'error',
          duration: 5000
        });
      }
    } finally {
      setGeneratingCertidaoEstadual(false);
    }
  };

  const handleViewCertidaoEstadual = (certidaoData) => {
    setCertidaoEstadualData(certidaoData);
    setShowCertidaoEstadual(true);
  };

  const handleGenerateCertidaoTrabalhista = async () => {
    if (!selectedRegiao) {
      toast({
        title: 'Região não selecionada',
        description: 'Selecione a região do TRT para gerar a certidão.',
        status: 'warning',
        duration: 5000
      });
      return;
    }

    setGeneratingCertidaoTrabalhista(true);

    try {
      const response = await api.post(
        `/contacted-precatorios/${state}/${precatorio._id}/certidao-trabalhista/generate`,
        { cpfCnpj: customCpfTrabalhista, regiao: parseInt(selectedRegiao) }
      );

      if (response.success) {
        toast({
          title: 'Certidão Trabalhista gerada',
          description: 'A Certidão Trabalhista foi gerada e anexada com sucesso.',
          status: 'success',
          duration: 5000
        });

        // Update local state
        setChecklist(prev => ({ ...prev, certidao_trabalhista: true }));

        // Refresh documents list
        await fetchDocuments();
      } else if (response.compromised) {
        const debitosMsg = response.debitos && response.debitos.length > 0
          ? ` Débitos: ${response.debitos.join(', ')}`
          : '';

        toast({
          title: 'Certidão Trabalhista compromissada',
          description: (response.message || 'Cliente com pendências trabalhistas.') + debitosMsg,
          status: 'warning',
          duration: 10000,
          isClosable: true
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao gerar certidão';

      if (error.response?.data?.compromised) {
        const debitosMsg = error.response?.data?.debitos && error.response.data.debitos.length > 0
          ? ` Débitos: ${error.response.data.debitos.join(', ')}`
          : '';

        toast({
          title: 'Certidão Trabalhista compromissada',
          description: 'Cliente com pendências trabalhistas.' + debitosMsg,
          status: 'warning',
          duration: 10000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Erro ao gerar Certidão Trabalhista',
          description: errorMessage,
          status: 'error',
          duration: 5000
        });
      }
    } finally {
      setGeneratingCertidaoTrabalhista(false);
    }
  };

  const isAllDocumentsComplete = () => {
    const required = getRequiredDocuments();
    return Object.keys(required).every(key => checklist[key] === true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await api.put(
        `/contacted-precatorios/${state}/${precatorio._id}/checklist`,
        { checklist }
      );

      if (response.success) {
        toast({
          title: 'Checklist atualizado',
          description: isAllDocumentsComplete() 
            ? 'Todos os documentos estão completos! Status atualizado para "Documentos Completos".'
            : 'Checklist salvo com sucesso.',
          status: 'success',
          duration: 3000
        });
        onUpdate(response.data);
        onClose();
      }
    } catch (error) {
      toast({
        title: 'Erro ao atualizar checklist',
        description: error.response?.data?.message || 'Erro ao salvar checklist',
        status: 'error',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const requiredDocs = getRequiredDocuments();
  const completedCount = Object.keys(requiredDocs).filter(key => checklist[key]).length;
  const totalCount = Object.keys(requiredDocs).length;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent maxW="95vw">
        <ModalHeader>
          Checklist de Documentos - {precatorio?.numero_precatorio}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Text fontWeight="bold">
                Tipo de Documento: {precatorio?.document_type_used?.toUpperCase()}
              </Text>
              <Badge colorScheme={completedCount === totalCount ? 'green' : 'yellow'}>
                {completedCount}/{totalCount} Completos
              </Badge>
            </HStack>

            <Divider />

            {completedCount === totalCount && (
              <Alert status="success">
                <AlertIcon />
                Todos os documentos estão completos! O status será atualizado automaticamente.
              </Alert>
            )}

            <Box>
              <Text mb={3} fontSize="sm" color="gray.600">
                Faça upload dos documentos necessários:
              </Text>
              
              {loadingDocs ? (
                <HStack justify="center" py={4}>
                  <Spinner />
                  <Text>Carregando documentos...</Text>
                </HStack>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                  {Object.entries(requiredDocs).map(([key, label]) => {
                    const doc = documents[key];
                    const isUploaded = doc?.uploaded || false;
                    const isUploading = uploadingDoc === key;

                    return (
                      <Box
                        key={key}
                        p={5}
                        minH="220px"
                        borderWidth="1px"
                        borderRadius="md"
                        bg={isUploaded ? "green.50" : "gray.50"}
                        borderColor={isUploaded ? "green.300" : "gray.200"}
                      >
                        <VStack align="stretch" spacing={3}>
                          {/* Title Row */}
                          <HStack spacing={3}>
                            {isUploaded ? (
                              <Checkbox
                                isChecked={true}
                                isDisabled={true}
                                colorScheme="green"
                                size="lg"
                              />
                            ) : (
                              <Box w="24px" h="24px" />
                            )}
                            <Text
                              fontSize="md"
                              fontWeight={isUploaded ? "bold" : "semibold"}
                              flex={1}
                            >
                              {label}
                            </Text>
                            {isUploaded && (
                              <Badge colorScheme="green">
                                <MdCheckCircle style={{ display: 'inline', marginRight: '4px' }} />
                                Enviado
                              </Badge>
                            )}
                          </HStack>

                          {/* File Info Row */}
                          {isUploaded && doc ? (
                            <Text fontSize="sm" color="green.600" pl="36px">
                              {doc.fileName} • {documentService.formatFileSize(doc.fileSize)}
                            </Text>
                          ) : (
                            <Text fontSize="sm" color="gray.500" pl="36px">
                              Documento pendente de envio
                            </Text>
                          )}

                          {/* Buttons Row */}
                          {!isUploaded ? (
                            <>
                              {key === 'certidao_federal' ? (
                                <VStack spacing={2} w="full">
                                  <FormControl>
                                    <FormLabel fontSize="sm">CPF/CNPJ para consulta:</FormLabel>
                                    <Input
                                      value={customCpfFederal}
                                      onChange={(e) => setCustomCpfFederal(e.target.value)}
                                      placeholder="Digite o CPF/CNPJ"
                                      size="sm"
                                    />
                                  </FormControl>
                                  <Button
                                    size="md"
                                    colorScheme="green"
                                    leftIcon={generatingCertidao ? <Spinner size="sm" /> : <MdUploadFile />}
                                    onClick={handleGenerateCertidaoFederal}
                                    isDisabled={generatingCertidao || !customCpfFederal}
                                    w="full"
                                  >
                                    {generatingCertidao ? 'Gerando...' : 'Gerar Certidão Federal'}
                                  </Button>
                                  <input
                                    type="file"
                                    ref={el => fileInputRefs.current['certidao_federal'] = el}
                                    style={{ display: 'none' }}
                                    accept=".pdf"
                                    onChange={(e) => handleFileUpload('certidao_federal', e.target.files[0])}
                                  />
                                  <Button
                                    size="md"
                                    colorScheme="blue"
                                    leftIcon={uploadingDoc === 'certidao_federal' ? <Spinner size="sm" /> : <MdUploadFile />}
                                    onClick={() => fileInputRefs.current['certidao_federal']?.click()}
                                    isDisabled={uploadingDoc === 'certidao_federal'}
                                    w="full"
                                  >
                                    {uploadingDoc === 'certidao_federal' ? 'Enviando...' : 'Enviar PDF'}
                                  </Button>
                                </VStack>
                              ) : key === 'certidao_estadual' ? (
                                <VStack spacing={2} w="full">
                                  <FormControl>
                                    <FormLabel fontSize="sm">CPF/CNPJ para consulta:</FormLabel>
                                    <Input
                                      value={customCpfEstadual}
                                      onChange={(e) => setCustomCpfEstadual(e.target.value)}
                                      placeholder="Digite o CPF/CNPJ"
                                      size="sm"
                                    />
                                  </FormControl>
                                  <Button
                                    size="md"
                                    colorScheme="green"
                                    leftIcon={generatingCertidaoEstadual ? <Spinner size="sm" /> : <MdUploadFile />}
                                    onClick={handleGenerateCertidaoEstadual}
                                    isDisabled={generatingCertidaoEstadual || !customCpfEstadual}
                                    w="full"
                                  >
                                    {generatingCertidaoEstadual ? 'Gerando...' : 'Gerar Certidão Estadual'}
                                  </Button>
                                  <input
                                    type="file"
                                    ref={el => fileInputRefs.current['certidao_estadual'] = el}
                                    style={{ display: 'none' }}
                                    accept=".pdf"
                                    onChange={(e) => handleFileUpload('certidao_estadual', e.target.files[0])}
                                  />
                                  <Button
                                    size="md"
                                    colorScheme="blue"
                                    leftIcon={uploadingDoc === 'certidao_estadual' ? <Spinner size="sm" /> : <MdUploadFile />}
                                    onClick={() => fileInputRefs.current['certidao_estadual']?.click()}
                                    isDisabled={uploadingDoc === 'certidao_estadual'}
                                    w="full"
                                  >
                                    {uploadingDoc === 'certidao_estadual' ? 'Enviando...' : 'Enviar PDF'}
                                  </Button>
                                </VStack>
                              ) : key === 'certidao_trabalhista' ? (
                                <VStack spacing={2} w="full">
                                  <FormControl>
                                    <FormLabel fontSize="sm">CPF/CNPJ para consulta:</FormLabel>
                                    <Input
                                      value={customCpfTrabalhista}
                                      onChange={(e) => setCustomCpfTrabalhista(e.target.value)}
                                      placeholder="Digite o CPF/CNPJ"
                                      size="sm"
                                    />
                                  </FormControl>
                                  <FormControl>
                                    <FormLabel fontSize="sm">Região TRT:</FormLabel>
                                    <Select
                                      value={selectedRegiao}
                                      onChange={(e) => setSelectedRegiao(e.target.value)}
                                      placeholder="Selecione a região"
                                      size="sm"
                                    >
                                      <option value="1">TRT 1ª Região - Rio de Janeiro</option>
                                      <option value="2">TRT 2ª Região - São Paulo (Capital)</option>
                                      <option value="3">TRT 3ª Região - Minas Gerais</option>
                                      <option value="4">TRT 4ª Região - Rio Grande do Sul</option>
                                      <option value="5">TRT 5ª Região - Bahia</option>
                                      <option value="6">TRT 6ª Região - Pernambuco</option>
                                      <option value="7">TRT 7ª Região - Ceará</option>
                                      <option value="8">TRT 8ª Região - Pará / Amapá</option>
                                      <option value="9">TRT 9ª Região - Paraná</option>
                                      <option value="10">TRT 10ª Região - Distrito Federal / Tocantins</option>
                                      <option value="11">TRT 11ª Região - Amazonas / Roraima</option>
                                      <option value="12">TRT 12ª Região - Santa Catarina</option>
                                      <option value="13">TRT 13ª Região - Paraíba</option>
                                      <option value="14">TRT 14ª Região - Acre / Rondônia</option>
                                      <option value="15">TRT 15ª Região - São Paulo (Campinas)</option>
                                      <option value="16">TRT 16ª Região - Maranhão</option>
                                      <option value="17">TRT 17ª Região - Espírito Santo</option>
                                      <option value="18">TRT 18ª Região - Goiás</option>
                                      <option value="19">TRT 19ª Região - Alagoas</option>
                                      <option value="20">TRT 20ª Região - Sergipe</option>
                                      <option value="21">TRT 21ª Região - Rio Grande do Norte</option>
                                      <option value="22">TRT 22ª Região - Piauí</option>
                                      <option value="23">TRT 23ª Região - Mato Grosso</option>
                                      <option value="24">TRT 24ª Região - Mato Grosso do Sul</option>
                                    </Select>
                                  </FormControl>
                                  <Button
                                    size="md"
                                    colorScheme="green"
                                    leftIcon={generatingCertidaoTrabalhista ? <Spinner size="sm" /> : <MdUploadFile />}
                                    onClick={handleGenerateCertidaoTrabalhista}
                                    isDisabled={generatingCertidaoTrabalhista || !customCpfTrabalhista || !selectedRegiao}
                                    w="full"
                                  >
                                    {generatingCertidaoTrabalhista ? 'Gerando...' : 'Gerar Certidão Trabalhista'}
                                  </Button>
                                  <input
                                    type="file"
                                    ref={el => fileInputRefs.current['certidao_trabalhista'] = el}
                                    style={{ display: 'none' }}
                                    accept=".pdf"
                                    onChange={(e) => handleFileUpload('certidao_trabalhista', e.target.files[0])}
                                  />
                                  <Button
                                    size="md"
                                    colorScheme="blue"
                                    leftIcon={uploadingDoc === 'certidao_trabalhista' ? <Spinner size="sm" /> : <MdUploadFile />}
                                    onClick={() => fileInputRefs.current['certidao_trabalhista']?.click()}
                                    isDisabled={uploadingDoc === 'certidao_trabalhista'}
                                    w="full"
                                  >
                                    {uploadingDoc === 'certidao_trabalhista' ? 'Enviando...' : 'Enviar PDF'}
                                  </Button>
                                </VStack>
                              ) : (
                                <>
                                  <input
                                    type="file"
                                    ref={el => fileInputRefs.current[key] = el}
                                    style={{ display: 'none' }}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleFileUpload(key, e.target.files[0])}
                                  />
                                  <Button
                                    size="md"
                                    colorScheme="blue"
                                    leftIcon={isUploading ? <Spinner size="sm" /> : <MdUploadFile />}
                                    onClick={() => fileInputRefs.current[key]?.click()}
                                    isDisabled={isUploading}
                                    w="full"
                                  >
                                    {isUploading ? 'Enviando...' : 'Enviar'}
                                  </Button>
                                </>
                              )}
                            </>
                          ) : (
                            <VStack spacing={2} w="full">
                              {/* Special handling for processo_execucao with risk analysis */}
                              {key === 'processo_execucao' && doc?.riskAnalysis && (
                                <>
                                  <Badge
                                    colorScheme={
                                      doc.riskAnalysis.band === 'low' ? 'green' :
                                      doc.riskAnalysis.band === 'medium' ? 'yellow' : 'red'
                                    }
                                    fontSize="sm"
                                    w="full"
                                    textAlign="center"
                                    py={1}
                                  >
                                    {doc.riskAnalysis.band === 'low' ? 'Baixo Risco' :
                                     doc.riskAnalysis.band === 'medium' ? 'Médio Risco' : 'Alto Risco'}
                                  </Badge>
                                  <Button
                                    size="sm"
                                    colorScheme="purple"
                                    w="full"
                                    onClick={handleViewRiskAnalysis}
                                    isLoading={loadingRiskAnalysis}
                                  >
                                    Ver Análise
                                  </Button>
                                </>
                              )}

                              {/* Special handling for certidao_estadual with certidao data */}
                              {key === 'certidao_estadual' && doc?.certidaoData && (
                                <>
                                  <Badge
                                    colorScheme={doc.certidaoData.possuiDebito ? 'red' : 'green'}
                                    fontSize="sm"
                                    w="full"
                                    textAlign="center"
                                    py={1}
                                  >
                                    {doc.certidaoData.possuiDebito ? 'Certidão Compromissada' : 'Certidão Limpa'}
                                  </Badge>
                                  <Button
                                    size="sm"
                                    colorScheme="purple"
                                    w="full"
                                    onClick={() => handleViewCertidaoEstadual(doc.certidaoData)}
                                  >
                                    Ver Certidão
                                  </Button>
                                </>
                              )}

                              <HStack spacing={2} justify="center" w="full">
                                <Tooltip label="Visualizar">
                                  <IconButton
                                    size="md"
                                    icon={<MdVisibility />}
                                    onClick={() => handleViewDocument(key)}
                                    aria-label="Visualizar documento"
                                  />
                                </Tooltip>
                                <Tooltip label="Baixar">
                                  <IconButton
                                    size="md"
                                    icon={<MdDownload />}
                                    onClick={() => handleDownloadDocument(key)}
                                    aria-label="Baixar documento"
                                  />
                                </Tooltip>
                                <Tooltip label="Excluir">
                                  <IconButton
                                    size="md"
                                    colorScheme="red"
                                    variant="ghost"
                                    icon={<MdDelete />}
                                    onClick={() => handleDeleteDocument(key)}
                                    aria-label="Excluir documento"
                                  />
                                </Tooltip>
                              </HStack>
                            </VStack>
                          )}
                        </VStack>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={loading}
          >
            Salvar Checklist
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Risk Analysis Modal */}
    {showRiskAnalysis && riskAnalysisData && (
      <RiskAnalysisModal
        isOpen={showRiskAnalysis}
        onClose={() => setShowRiskAnalysis(false)}
        analysisData={riskAnalysisData}
        precatorioId={precatorio?._id}
        state={state}
      />
    )}

    {/* Certidao Estadual Modal */}
    {showCertidaoEstadual && certidaoEstadualData && (
      <CertidaoEstadualModal
        isOpen={showCertidaoEstadual}
        onClose={() => setShowCertidaoEstadual(false)}
        certidaoData={certidaoEstadualData}
        precatorioId={precatorio?._id}
        state={state}
      />
    )}

    {/* Document Viewer Modal */}
    {viewDocument && (
      <DocumentViewer
        isOpen={!!viewDocument}
        onClose={() => setViewDocument(null)}
        documentUrl={viewDocument.url}
        fileName={viewDocument.fileName}
        fileType={viewDocument.fileType}
      />
    )}
    </>
  );
};

export default DocumentChecklistModal;